/**
 * User Create Page
 * Page for creating a new user with multi-step form
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

// Components
import { UserForm } from './components';
import { PageShell } from '../../components';

// Services and types
import { userService } from '../../services/userService';
import {
  UserFormData,
  UserEditFormData,
  CreateUserRequest,
  BindingEntityType,
  removeCPFMask,
  removeWhatsAppMask,
} from '../../types/user';

const UserCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Handle form submission
  const handleSubmit = async (data: UserFormData | UserEditFormData) => {
    const formData = data as UserFormData;
    setLoading(true);
    setError(null);

    try {
      console.log('[UserCreatePage] Creating user:', formData);

      // Build request
      const request: CreateUserRequest = {
        name: formData.name,
        email: formData.email,
        cpf: removeCPFMask(formData.cpf),
        password: formData.password ?? undefined,
        whatsapp: formData.whatsapp ? removeWhatsAppMask(formData.whatsapp) : undefined,
      };

      // Add initial binding if provided
      if (formData.bindingEntityType && formData.bindingEntityId) {
        if (formData.bindingEntityType === BindingEntityType.COMPANY) {
          request.companyId = parseInt(formData.bindingEntityId);
        } else if (formData.bindingEntityType === BindingEntityType.PROVIDER) {
          request.providerId = parseInt(formData.bindingEntityId);
        }
        if (formData.bindingFunction) {
          request.bindingFunction = formData.bindingFunction;
        }
      }

      // Os papéis vão no mesmo POST: criar o usuário e conceder o papel em duas chamadas deixaria
      // gente sem acesso nenhum se a segunda falhasse.
      if (formData.roleIds && formData.roleIds.length > 0) {
        request.roleIds = formData.roleIds;
      }

      const response = await userService.createUser(request);

      console.log('[UserCreatePage] User created:', response);

      // Show success message
      if (response.temporaryPassword) {
        enqueueSnackbar('Usuário criado! Uma senha temporária foi enviada por e-mail.', {
          variant: 'success',
        });
      } else {
        enqueueSnackbar('Usuário criado com sucesso!', { variant: 'success' });
      }

      // Navigate to user details
      navigate(`/users/${response.user.id}`);
    } catch (err: unknown) {
      console.error('[UserCreatePage] Error creating user:', err);
      const errorMessage = (err as { message?: string }).message ?? 'Erro ao criar usuário';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/users');
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Usuários', path: '/users' },
        { label: 'Novo Usuário' },
      ]}
      title="Cadastrar Novo Usuário"
      subtitle="Preencha os dados abaixo para criar um novo usuário no sistema"
    >
      {/* Form */}
      <UserForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        loading={loading}
        error={error}
      />
    </PageShell>
  );
};

export default UserCreatePage;
